"use client";

import { Activity, EyeOff, Globe2, RadioTower, SlidersHorizontal } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/components/auth/auth-provider";
import { AnarchyGlobe } from "@/components/globe/anarchy-globe";
import { SiteShell } from "@/components/layout/site-shell";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { useGlobeFrameStream } from "@/hooks/use-globe-frame-stream";
import {
  DEFAULT_GDELT_MOCK_FRAME_URL,
  GEO_PRESSURE_TIME_WINDOWS,
  HOT_ZONE_CATEGORIES,
  HOT_ZONE_CATEGORY_STYLE,
  buildGeoPressureIntelligenceHref,
  filterGlobeFrame,
  globeCameraStateToSearchParams,
  parseGeoPressureFilters,
  parseGlobeCameraUrlState,
  resolveGlobeStreamSource,
  type GlobeCameraUrlState,
  type GeoPressureFilterState,
} from "@/lib/gdelt-globe";
import { cn } from "@/lib/utils";
import type { GlobeFrame, GlobeMapMode, HotZone } from "@/types/gdelt";

type GeoPressureIntelligenceClientProps = {
  initialFrame: GlobeFrame;
  initialFilters: GeoPressureFilterState;
  initialCameraState?: GlobeCameraUrlState | null;
  initialSourceUrl?: string;
  initialMapMode?: GlobeMapMode;
};

const DETAIL_ROLES = ["administrator", "super_admin", "tenant_admin", "developer", "editor"] as const;

export function GeoPressureIntelligenceClient({
  initialFrame,
  initialFilters,
  initialCameraState = null,
  initialSourceUrl,
  initialMapMode = "outline",
}: GeoPressureIntelligenceClientProps) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const auth = useAuth();
  const [filters, setFilters] = useState(initialFilters);
  const [cameraState, setCameraState] = useState<GlobeCameraUrlState | null>(initialCameraState);

  const source = useMemo(
    () => resolveGlobeStreamSource(searchParams.get("source") ?? initialSourceUrl ?? DEFAULT_GDELT_MOCK_FRAME_URL),
    [initialSourceUrl, searchParams],
  );
  const mapMode: GlobeMapMode = searchParams.get("mode") === "terrain" ? "terrain" : initialMapMode;
  const { frame, status, error } = useGlobeFrameStream(source, initialFrame);

  useEffect(() => {
    const params = Object.fromEntries(searchParams.entries());
    setFilters(parseGeoPressureFilters(params));
    setCameraState(parseGlobeCameraUrlState(params));
  }, [searchParams]);

  const filteredFrame = useMemo(() => {
    if (!frame) return null;
    return {
      ...filterGlobeFrame(frame, filters),
      window_minutes: filters.tWindowMinutes,
    };
  }, [filters, frame]);

  const selectedZone = useMemo(() => {
    if (!filteredFrame || !filters.zoneId) return null;
    return filteredFrame.zones.find((zone) => zone.zone_id === filters.zoneId) ?? null;
  }, [filteredFrame, filters.zoneId]);

  const canViewDetails = auth.isAuthenticated && auth.hasAnyRole([...DETAIL_ROLES]);
  const canViewSensitiveMetrics = auth.canViewEmail();

  const updateFilters = (nextFilters: GeoPressureFilterState) => {
    setFilters(nextFilters);
    const href = buildGeoPressureIntelligenceHref(pathname, nextFilters, {
      source: source.url === DEFAULT_GDELT_MOCK_FRAME_URL ? null : source.url,
      mode: mapMode === "outline" ? null : mapMode,
      ...globeCameraStateToSearchParams(cameraState),
    });
    navigate(href, { replace: true });
  };

  const handleZoneSelect = (zone: HotZone) => {
    updateFilters({ ...filters, zoneId: zone.zone_id });
  };

  const handleCameraStateChange = (nextCameraState: GlobeCameraUrlState) => {
    setCameraState(nextCameraState);
    const href = buildGeoPressureIntelligenceHref(pathname, filters, {
      source: source.url === DEFAULT_GDELT_MOCK_FRAME_URL ? null : source.url,
      mode: mapMode === "outline" ? null : mapMode,
      ...globeCameraStateToSearchParams(nextCameraState),
    });
    window.history.replaceState(null, "", href);
  };

  return (
    <SiteShell
      map={
        <AnarchyGlobe
          frame={filteredFrame ?? initialFrame}
          mapMode={mapMode}
          selectedZoneId={filters.zoneId}
          onZoneSelect={handleZoneSelect}
          initialCameraState={cameraState}
          onCameraStateChange={handleCameraStateChange}
          showTelemetryOverlay={false}
          className="h-full min-h-[72vh] rounded-none border-0 lg:min-h-0"
        />
      }
      sidebar={
        <aside className="space-y-4">
          <section className="rounded-md border border-white/10 bg-card p-4">
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-muted-foreground">
              <Globe2 className="h-3.5 w-3.5" aria-hidden />
              Global Pressure
            </div>
            <h1 className="mt-2 text-xl font-semibold">Intelligence globe</h1>
            <div className="mt-3 grid grid-cols-3 gap-2 text-center">
              <Metric label="Zones" value={filteredFrame?.zones.length ?? 0} />
              <Metric label="Window" value={formatWindow(filters.tWindowMinutes)} />
              <Metric label="Intensity" value={filteredFrame?.global_intensity ?? initialFrame.global_intensity} />
            </div>
            <div className="mt-3 flex items-center gap-2 rounded-sm border border-cyan-300/15 bg-cyan-300/5 px-3 py-2 text-xs text-cyan-100">
              <RadioTower className="h-3.5 w-3.5 shrink-0" aria-hidden />
              <span className="truncate">{error ? error : status === "open" ? source.url : "Connecting stream"}</span>
            </div>
          </section>

          <section className="rounded-md border border-white/10 bg-card p-4">
            <div className="flex items-center gap-2 text-sm font-medium">
              <SlidersHorizontal className="h-4 w-4" aria-hidden />
              Filters
            </div>

            <div className="mt-4 space-y-3">
              <ControlLabel label="Event category" value={filters.category === "all" ? "All" : HOT_ZONE_CATEGORY_STYLE[filters.category].label} />
              <div className="grid grid-cols-2 gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant={filters.category === "all" ? "secondary" : "outline"}
                  onClick={() => updateFilters({ ...filters, category: "all", zoneId: null })}
                >
                  All
                </Button>
                {HOT_ZONE_CATEGORIES.map((category) => (
                  <Button
                    key={category}
                    type="button"
                    size="sm"
                    variant={filters.category === category ? "secondary" : "outline"}
                    className="justify-start"
                    onClick={() => updateFilters({ ...filters, category, zoneId: null })}
                  >
                    <span
                      className="h-2 w-2 rounded-full shadow-[0_0_14px_currentColor]"
                      style={{
                        color: HOT_ZONE_CATEGORY_STYLE[category].glow,
                        backgroundColor: HOT_ZONE_CATEGORY_STYLE[category].glow,
                      }}
                    />
                    <span className="truncate">{HOT_ZONE_CATEGORY_STYLE[category].label}</span>
                  </Button>
                ))}
              </div>
            </div>

            <div className="mt-5 space-y-3">
              <ControlLabel label="Time window" value={formatWindow(filters.tWindowMinutes)} />
              <div className="grid grid-cols-4 gap-2">
                {GEO_PRESSURE_TIME_WINDOWS.map((window) => (
                  <Button
                    key={window.minutes}
                    type="button"
                    size="sm"
                    variant={filters.tWindowMinutes === window.minutes ? "secondary" : "outline"}
                    onClick={() => updateFilters({ ...filters, tWindowMinutes: window.minutes })}
                  >
                    {window.label}
                  </Button>
                ))}
              </div>
              <Button type="button" size="sm" variant="ghost" disabled className="w-full justify-start">
                Longer windows pending stream behavior
              </Button>
            </div>

            <div className="mt-5 space-y-3">
              <ControlLabel label="Intensity threshold" value={`${filters.minIntensity}+`} />
              <Slider
                min={0}
                max={100}
                step={5}
                value={filters.minIntensity}
                aria-label="Intensity threshold"
                onValueChange={(value) => updateFilters({ ...filters, minIntensity: value, zoneId: null })}
              />
            </div>
          </section>

          <section
            className={cn(
              "rounded-md border p-4 transition",
              selectedZone ? "border-cyan-300/30 bg-cyan-300/[0.06]" : "border-white/10 bg-card",
            )}
          >
            {selectedZone ? (
              <ZoneInfoCard zone={selectedZone} canViewDetails={canViewDetails} canViewSensitiveMetrics={canViewSensitiveMetrics} />
            ) : (
              <div className="py-4 text-sm text-muted-foreground">
                Left-click a hot zone to select it, fly the camera to its centroid, and open intelligence cards here.
              </div>
            )}
          </section>
        </aside>
      }
    />
  );
}

function ZoneInfoCard({
  zone,
  canViewDetails,
  canViewSensitiveMetrics,
}: {
  zone: HotZone;
  canViewDetails: boolean;
  canViewSensitiveMetrics: boolean;
}) {
  const style = HOT_ZONE_CATEGORY_STYLE[zone.category];

  return (
    <div>
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-muted-foreground">
            <Activity className="h-3.5 w-3.5" aria-hidden />
            Selected zone
          </div>
          <h2 className="mt-1 break-all text-lg font-semibold">{zone.zone_id}</h2>
        </div>
        <span className="rounded-sm px-2 py-1 text-xs font-medium text-black" style={{ backgroundColor: style.glow }}>
          {zone.trend}
        </span>
      </div>

      <div className="mt-3 grid grid-cols-3 gap-2 text-center">
        <Metric label="Intensity" value={zone.intensity} />
        <Metric label="Events" value={zone.event_count} />
        <Metric label="Mentions" value={canViewSensitiveMetrics ? zone.mention_count : "Hidden"} />
      </div>

      <div className="mt-3 flex flex-wrap gap-1.5">
        {zone.top_themes.map((theme) => (
          <span key={theme} className="rounded-sm bg-white/10 px-2 py-1 text-xs text-muted-foreground">
            {theme}
          </span>
        ))}
      </div>

      {canViewDetails ? (
        <div className="mt-4 grid gap-2 text-sm">
          <DetailRow label="Category" value={style.label} />
          <DetailRow label="Geohash" value={zone.geohash} />
          <DetailRow label="Centroid" value={`${zone.centroid.lat.toFixed(3)}, ${zone.centroid.lon.toFixed(3)}`} />
          <DetailRow label="Updated" value={new Date(zone.updated_at).toLocaleString()} />
        </div>
      ) : (
        <div className="mt-4 flex gap-2 rounded-sm border border-white/10 bg-black/20 px-3 py-2 text-xs text-muted-foreground">
          <EyeOff className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
          Sign in with an authorized role to view geohash, exact centroid, and source-level metrics.
        </div>
      )}
    </div>
  );
}

function ControlLabel({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-3 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium text-foreground">{value}</span>
    </div>
  );
}

function Metric({ label, value }: { label: string; value: number | string }) {
  return (
    <div className="rounded-sm border border-white/10 bg-white/[0.04] px-2 py-2">
      <p className="truncate text-base font-semibold">{value}</p>
      <p className="text-[0.68rem] uppercase tracking-[0.14em] text-muted-foreground">{label}</p>
    </div>
  );
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-start justify-between gap-3 rounded-sm border border-white/10 bg-white/[0.03] px-3 py-2">
      <span className="text-muted-foreground">{label}</span>
      <span className="break-all text-right font-medium">{value}</span>
    </div>
  );
}

function formatWindow(minutes: number) {
  if (minutes < 60) return `${minutes}m`;
  if (minutes % 60 === 0 && minutes < 1440) return `${minutes / 60}h`;
  if (minutes === 1440) return "24h";
  return `${minutes}m`;
}
